import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal,
  Button,
  Typography,
  Table,
  Input,
  Form,
  useToast,
  Textarea,
} from '@crisellevaldez/speculo-ui';
import { Pencil, Trash } from 'lucide-react';
import { ConfirmationDialog } from '../../common/dialogs';
import { Organization } from '../../../types/organization.types';
import {
  useGeneralKnowledgeBase,
  KnowledgeBaseItem,
} from '../../../hooks/client/useGeneralKnowledgeBase';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { knowledgeBaseSchema, KnowledgeBaseFormData } from '../../../schemas/knowledgeBase.schema';
import { useUser } from '../../../hooks/useUser';
import { AdminLockOverlay } from './AdminLockOverlay';

export interface GeneralKnowledgeBaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg: Organization | undefined;
  onSuccess?: () => void;
}

export const GeneralKnowledgeBaseModal: React.FC<GeneralKnowledgeBaseModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  const { showToast } = useToast();
  const { isAdmin } = useUser();
  const {
    data: knowledgeBases = [],
    isLoading,
    addKnowledgeBase,
    refetch,
  } = useGeneralKnowledgeBase(selectedOrg?.id, isOpen);

  const form = useForm<KnowledgeBaseFormData>({
    resolver: zodResolver(knowledgeBaseSchema),
    defaultValues: {
      question: '',
      answer: '',
    },
  });

  const [itemToDelete, setItemToDelete] = useState<KnowledgeBaseItem | null>(null);
  const [itemToEdit, setItemToEdit] = useState<KnowledgeBaseItem | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const currentKnowledgeBase = knowledgeBases[0];

  const handleDeleteClick = useCallback((item: KnowledgeBaseItem) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  }, []);

  const handleEditClick = useCallback(
    (item: KnowledgeBaseItem) => {
      setItemToEdit(item);
      form.reset({
        question: item.question,
        answer: item.answer,
      });
    },
    [currentKnowledgeBase?.kb_name, form]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!currentKnowledgeBase?.id || !itemToDelete || !selectedOrg?.id) return;

    setIsDeleting(true);
    try {
      await addKnowledgeBase.mutateAsync({
        id: currentKnowledgeBase.id,
        kb_name: currentKnowledgeBase.kb_name,
        knowledge_base: currentKnowledgeBase.knowledge_base.filter(
          (item) => item.question !== itemToDelete.question || item.answer !== itemToDelete.answer
        ),
        organization_id: selectedOrg.id,
        type: 'general',
      });

      showToast({
        message: 'Question deleted successfully.',
        variant: 'success',
      });
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to delete question.',
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [currentKnowledgeBase, itemToDelete, selectedOrg?.id, addKnowledgeBase, showToast]);

  useEffect(() => {
    if (isOpen) {
      setItemToEdit(null); // Reset edit state when modal opens
      form.reset({
        question: '',
        answer: '',
      });
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveKnowledgeBase = async (data: KnowledgeBaseFormData) => {
    if (!selectedOrg?.id) return;

    // Always use org_name + FAQ as the KB name
    const kbName = `${selectedOrg.org_name} FAQ`;

    try {
      if (itemToEdit) {
        // Update existing question
        if (!currentKnowledgeBase?.id) return;

        const updatedKnowledgeBase = currentKnowledgeBase.knowledge_base.map((item) => {
          if (item.question === itemToEdit.question && item.answer === itemToEdit.answer) {
            return { question: data.question, answer: data.answer };
          }
          return item;
        });

        await addKnowledgeBase.mutateAsync({
          id: currentKnowledgeBase.id,
          kb_name: kbName,
          knowledge_base: updatedKnowledgeBase,
          organization_id: selectedOrg.id,
          type: 'general',
        });

        setItemToEdit(null);
        showToast({
          message: 'Question updated successfully.',
          variant: 'success',
        });
      } else {
        // Add new question
        const newQuestion = { question: data.question, answer: data.answer };

        if (currentKnowledgeBase?.id) {
          await addKnowledgeBase.mutateAsync({
            id: currentKnowledgeBase.id,
            kb_name: kbName,
            knowledge_base: [...currentKnowledgeBase.knowledge_base, newQuestion],
            organization_id: selectedOrg.id,
            type: 'general',
          });
        } else {
          await addKnowledgeBase.mutateAsync({
            kb_name: kbName,
            knowledge_base: [newQuestion],
            organization_id: selectedOrg.id,
            type: 'general',
          });
        }

        showToast({
          message: 'Question added successfully.',
          variant: 'success',
        });
      }

      await refetch();
      form.reset({
        question: '',
        answer: '',
      });
      onSuccess?.();
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to save knowledge base.',
        variant: 'error',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <Modal.Header>
        <div className="flex items-center justify-between w-full">
          <Typography variant="h3">General Knowledge Base</Typography>
        </div>
      </Modal.Header>
      <Modal.Body className="py-8 flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 min-h-0 overflow-auto">
          <Table<KnowledgeBaseItem>
              data={currentKnowledgeBase?.knowledge_base || []}
              columns={[
                {
                  key: 'question',
                  header: 'QUESTION',
                  width: '300px',
                  cell: (row: KnowledgeBaseItem) => row.question,
                },
                {
                  key: 'answer',
                  header: 'ANSWER',
                  width: '600px',
                  cell: (row: KnowledgeBaseItem) => row.answer,
                },
                {
                  key: 'actions',
                  header: 'ACTIONS',
                  width: '100px',
                  isCentered: true,
                  isPinned: true,
                  pinPosition: 'right',
                  // Edit/Delete stay visible for agents but are disabled —
                  // editing the knowledge base is admin-only.
                  cell: (row: KnowledgeBaseItem) => (
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditClick(row)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-white/10"
                        disabled={isDeleting || !isAdmin}
                        title={!isAdmin ? 'Only your organization admin can edit the knowledge base' : undefined}
                      >
                        <Pencil className="w-3.5 h-3.5 text-gray-800 dark:text-gray-200" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteClick(row)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-white/10"
                        disabled={isDeleting || !isAdmin}
                        title={!isAdmin ? 'Only your organization admin can edit the knowledge base' : undefined}
                      >
                        <Trash className="w-3.5 h-3.5 text-gray-800 dark:text-gray-200" />
                      </Button>
                    </div>
                  ),
                },
              ]}
              loading={isLoading}
              keyExtractor={(item) => `${item.question}-${item.answer}`}
              emptyStateText="No questions added yet"
            />
          </div>
      </Modal.Body>
      <Modal.Footer>
        <AdminLockOverlay locked={!isAdmin} subject="The knowledge base">
        <form onSubmit={form.handleSubmit(handleSaveKnowledgeBase)} className="w-full" noValidate>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Form.Control required>
                <Form.Label>Question</Form.Label>
                <Controller
                  control={form.control}
                  name="question"
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      error={form.formState.errors.question?.message}
                      placeholder="Enter question"
                      rows={3}
                    />
                  )}
                />
              </Form.Control>
              <Form.Control required>
                <Form.Label>Answer</Form.Label>
                <Controller
                  control={form.control}
                  name="answer"
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      error={form.formState.errors.answer?.message}
                      placeholder="Enter answer"
                      rows={3}
                    />
                  )}
                />
              </Form.Control>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  if (itemToEdit) {
                    setItemToEdit(null);
                    form.reset({
                      question: '',
                      answer: '',
                    });
                  } else {
                    onClose();
                  }
                }}
                type="button"
              >
                {itemToEdit ? 'Cancel Edit' : 'Cancel'}
              </Button>
              <Button
                variant="primary"
                type="submit"
                isLoading={addKnowledgeBase.isPending}
                disabled={addKnowledgeBase.isPending}
              >
                {itemToEdit ? 'Update Question' : 'Add Question'}
              </Button>
            </div>
          </div>
        </form>
        </AdminLockOverlay>
      </Modal.Footer>
      <ConfirmationDialog
        isOpen={showDeleteModal}
        onClose={() => {
          if (!isDeleting) {
            setShowDeleteModal(false);
            setItemToDelete(null);
          }
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Question"
        message={`Are you sure you want to delete this question?`}
        confirmLabel="Delete"
        isLoading={isDeleting}
        variant="danger"
      />
    </Modal>
  );
};
